import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {Text, useToast} from "components-lib";
import {
    fetchParametersByAppointment,
    fetchParametersByDoctor,
    loadAppointmentParametersFromLocalStorage,
    saveAppointmentParameters,
    saveAppointmentParametersToLocalStorage
} from "services/parametersService.js";

const MIN_FETCH_DELAY_MS = 100;

// Delay mounting of the component to reduce initial load on server/UI
const COMPONENT_MOUNT_DELAY_MS = 500; // 500ms after mount
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * ParametersSection Component
 * Handles dynamic patient parameters data input and management
 * Uses parametersService functions to fetch, load, save and display parameters
 */
const ParametersSection = ({
                               appointment_id,
                               doctor_id,
                               onAddparameters,
                               setparametersList,
                               appointmentPrescription,
                               handleSubmit
                           }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [parameterDefinitions, setParameterDefinitions] = useState([]);
    const [parameterValues, setParameterValues] = useState({});
    const [originalParameterValues, setOriginalParameterValues] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // Delay the actual initialization/rendering of this component
    const [delayedMounted, setDelayedMounted] = useState(false);
    const toast = useToast();

    const hasPrescriptionParameters = Boolean(
        appointmentPrescription?.parameters &&
        appointmentPrescription.parameters.length > 0
    ); 

    const doctorDisplayName = useMemo(() => {
        const candidate =
            appointmentPrescription?.doctor?.display_name ||
            appointmentPrescription?.doctor?.full_name ||
            appointmentPrescription?.doctor?.name ||
            appointmentPrescription?.doctor_name;
        if (candidate) return candidate;
        if (doctor_id) return `Doctor ${doctor_id}`;
        return "Doctor";
    }, [appointmentPrescription, doctor_id]);

    useEffect(() => {
        // fetch parameters from api and compare with local ones; if mismatch set hasUnsavedChanges to true and raise a toast
        (async () => {
            if (!appointment_id) return;

            try {
                const localParams =
                    loadAppointmentParametersFromLocalStorage(appointment_id) || {};

                if (localParams === null || Object.keys(localParams).length === 0) {
                    // No local params to compare
                    return;
                }

                const serverParamsArray = await fetchParametersByAppointment(
                    appointment_id
                );
                const serverParams = Array.isArray(serverParamsArray)
                    ? serverParamsArray.reduce((acc, p) => {
                        const key = String(p.parameter_id ?? p.id ?? "");
                        if (key) acc[key] = p.value ?? "";
                        return acc;
                    }, {})
                    : {};

                // Build combined key set and compare values (stringified)
                const keys = new Set([
                    ...Object.keys(localParams),
                    ...Object.keys(serverParams),
                ]);
                let mismatch = false;
                for (const k of keys) {
                    const localVal =
                        localParams[k] === undefined || localParams[k] === null
                            ? ""
                            : String(localParams[k]);
                    const serverVal =
                        serverParams[k] === undefined || serverParams[k] === null
                            ? ""
                            : String(serverParams[k]);
                    if (localVal != serverVal) {
                        mismatch = true;
                        break;
                    }
                }

                if (mismatch) {
                    setHasUnsavedChanges(true);

                    toast({
                        title: "Unsaved changes detected",
                        description:
                            "Local parameter values differ from the server. Please review and save if needed.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                }
            } catch (err) {
                console.warn("Error while comparing local and server parameters:", err);
            }
        })();
    }, [appointment_id]);

    // Debug logging
    useEffect(() => {
        console.log("ParametersSection mounted with:", {
            appointment_id,
            doctor_id,
            hasOnAddParameters: !!onAddparameters,
            hasSetParametersList: !!setparametersList,
        });
    }, [appointment_id, doctor_id, onAddparameters, setparametersList]);


    // Initialize parameters data using parametersService functions
    const initializeParameters = useCallback(async () => {
        if (!doctor_id) {
            console.warn("No doctor_id provided, cannot fetch parameters");
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            console.log("Initializing parameters for appointment:", appointment_id);

            // Always fetch the doctor's parameter definitions so we can render all inputs
            console.log("Delaying before fetching parameter definitions to reduce server load");
            await delay(MIN_FETCH_DELAY_MS);

            console.log("Fetching parameter definitions for doctor_id:", doctor_id);
            const definitions = await fetchParametersByDoctor(doctor_id);
            console.log("Fetched parameter definitions:", definitions);

            if (!Array.isArray(definitions)) {
                throw new Error("Invalid parameter definitions format");
            }

            setParameterDefinitions(definitions);

            // Initialize values for all definitions
            const initialValues = {};
            definitions.forEach((param) => {
                initialValues[param.id] = "";
            });

            // If appointmentPrescription has parameter values, overlay them on the inputs
            if (appointmentPrescription?.parameters && Array.isArray(appointmentPrescription.parameters)) {
                appointmentPrescription.parameters.forEach((p) => {
                    const key = String(p.parameter_id ?? p.id ?? "");
                    if (key && initialValues.hasOwnProperty(key)) {
                        initialValues[key] = p.value ?? "";
                    }
                });
            }

            // Load saved parameters from localStorage and overlay
            if (appointment_id) {
                console.log(
                    "Loading parameters from localStorage for appointment:",
                    appointment_id
                );
                const savedParameters = loadAppointmentParametersFromLocalStorage(
                    appointment_id
                );

                if (savedParameters && typeof savedParameters === "object") {
                    Object.keys(savedParameters).forEach((key) => {
                        if (initialValues.hasOwnProperty(key)) {
                            initialValues[key] = savedParameters[key];
                        }
                    });
                }

                // Try to fetch server-saved parameters and overlay if values are empty
                try {
                    console.log(
                        "Fetching existing server parameters for appointment:",
                        appointment_id
                    );
                    const serverParameters = await fetchParametersByAppointment(
                        appointment_id
                    );
                    console.log("Server parameters:", serverParameters);

                    if (Array.isArray(serverParameters)) {
                        serverParameters.forEach((serverParam) => {
                            const key = String(serverParam.parameter_id ?? serverParam.id ?? "");
                            if (key && initialValues.hasOwnProperty(key)) {
                                // Only fill if current value is empty
                                if (!initialValues[key]) {
                                    initialValues[key] = serverParam.value || "";
                                }
                            }
                        });
                    }
                } catch (serverError) {
                    console.warn("Could not fetch server parameters:", serverError);
                }
            }

            setParameterValues(initialValues);
            setOriginalParameterValues(initialValues);
            setHasUnsavedChanges(false);
        } catch (error) {
            console.error("Error initializing parameters:", error);
            setError(error.message || "Failed to load parameters");

            toast({
                title: "Error",
                description: "Failed to load parameters. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    }, [doctor_id, appointment_id, toast, appointmentPrescription]);

    // Delay init slightly to avoid heavy work during immediate mount
    useEffect(() => {
        const t = setTimeout(() => setDelayedMounted(true), COMPONENT_MOUNT_DELAY_MS);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        if (!delayedMounted) return;
        initializeParameters();
    }, [initializeParameters, delayedMounted]);

    // NOTE: auto-save on unmount is disabled. Parameters are persisted to localStorage
    // on every change, and are flushed to the server explicitly when the consultation
    // is ended (see `flushLocalParametersToServer` in services/parametersService.js).

    // Validate parameter value based on its type and constraints
    const validateParameterValue = (value, parameter) => {
        if (!value && parameter.is_required) {
            return "This field is required";
        }

        // No additional validation; allow any string including alphanumeric, special characters, etc.
        return ""; // No error
    };

    // Handle parameter value changes with proper localStorage saving
    const handleParameterChange = useCallback(
        (parameterId, value, parameter) => {
            console.log(`Parameter ${parameterId} changed to:`, value);

            // Update state with new value (as string)
            setParameterValues((prev) => {
                const updated = {
                    ...prev,
                    [parameterId]: value,
                };

                // Save to localStorage using parametersService function in the correct format
                if (appointment_id) {
                    saveAppointmentParametersToLocalStorage(appointment_id, updated);
                }

                return updated;
            });

            setHasUnsavedChanges(true);
        },
        [appointment_id]
    );

    // Handle form submission using parametersService functions
    handleSubmit = async (e) => {
        e?.preventDefault();

        console.log("Form submission started with values:", parameterValues);

        if (!appointment_id) {
            const errorMsg = "Cannot save parameters: No appointment ID provided";
            console.error(errorMsg);
            toast({
                title: "Error",
                description: errorMsg,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
            return false;
        }

        setIsSaving(true);
        setError(null);

        try {
            // Prepare parameters array in the format expected by saveAppointmentParameters
            const parametersToSave = Object.entries(parameterValues)
                .filter(
                    ([_, value]) => value !== "" && value !== null && value !== undefined
                )
                .map(([parameter_id, value]) => ({
                    parameter_id: parseInt(parameter_id, 10),
                    value: String(value).trim(),
                }));

            console.log("Prepared parameters for saving:", parametersToSave);

            if (parametersToSave.length === 0) {
                const errorMsg = "No parameters to save";
                console.warn(errorMsg);
                toast({
                    title: "Warning",
                    description: errorMsg,
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                return false;
            }

            // Save parameters using parametersService function
            console.log("Saving parameters for appointment:", appointment_id);
            const response = await saveAppointmentParameters(
                appointment_id,
                parametersToSave
            );
            console.log("Save response:", response);

            // Update parent state if callbacks are provided
            if (onAddparameters) {
                console.log("Calling onAddparameters callback");
                onAddparameters(parameterValues);
            }

            if (setparametersList) {
                console.log("Updating parameters list");
                setparametersList((prev) => [...prev, parameterValues]);
            }

            setHasUnsavedChanges(false);
            setOriginalParameterValues({...parameterValues});
            console.log("Parameters saved successfully");

            toast({
                title: "Success",
                description: "Parameters saved successfully",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            return true;
        } catch (error) {
            console.error("Error in handleSubmit:", error);
            const errorMessage =
                error.response?.data?.message ||
                error.message ||
                "Failed to save parameters";
            setError(errorMessage);

            // toast({
            //   title: "Error",
            //   description: errorMessage,
            //   status: "error",
            //   duration: 5000,
            //   isClosable: true,
            // });

            return false;
        } finally {
            setIsSaving(false);
        }
    };

    // Render parameter table rows based on fetchParametersByDoctor format
    const renderParameterTable = () => {
        if (parameterDefinitions.length === 0) return null;

        // Split parameters into two rows for better display
        const midPoint = Math.ceil(parameterDefinitions.length / 2);
        const firstRow = parameterDefinitions.slice(0, midPoint);
        const secondRow = parameterDefinitions.slice(midPoint);

        const renderParameterInput = (param) => {
            const value = parameterValues[param.id] || "";
            const hasValue = String(value).trim().length > 0;
            const error = validateParameterValue(value, param);
            const inputType = "text"; // Treat all as text to allow any characters
            const inputProps = {}; // Remove number-specific props
            const showPatientHint = hasPrescriptionParameters && !hasValue;
            const unitLabel = param.unit && param.unit !== "d" ? ` (${param.unit})` : "";
            const placeholderSuffix = doctorDisplayName ? ` ` : "";

            const originalValue = originalParameterValues?.[param.id];
            const currentTrim = String(value || "").trim();
            const originalTrim =
                originalValue !== undefined && originalValue !== null
                    ? String(originalValue).trim()
                    : "";
            const hasUnsavedChangesForField = currentTrim !== originalTrim;
            const defaultBorderColor = "#E2E8F0";
            const unsavedBorderColor = "#2B6CB0";
            const errorBorderColor = "#E53E3E";
            const computedBorderColor = error
                ? errorBorderColor
                : hasUnsavedChangesForField
                    ? unsavedBorderColor
                    : defaultBorderColor;
            const borderWidth = hasUnsavedChangesForField && !error ? 2 : 1;
            const borderStyle = `${borderWidth}px solid ${computedBorderColor}`;

            return (
                <td
                    key={param.id}
                    style={{
                        border: "none",
                        padding: "10px",
                        background: "white",
                        textAlign: "left",
                        verticalAlign: "top",
                        boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
                        borderRadius: "10px",
                    }}
                >
                    <div style={{marginBottom: "8px"}}>
                        <label
                            style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginBottom: "4px",
                                display: "block",
                            }}
                        >
                            <div>
                                {param.name}
                                {unitLabel}
                                {param.is_required && (
                                    <span style={{color: "red", marginLeft: "4px"}}>*</span>
                                )}
                            </div>
                            <div style={{fontSize: "12px", color: "#4A5568"}}>

                            </div>
                        </label>
                        <div style={{position: "relative"}}>
                            <input
                                type={inputType}
                                value={value}
                                onChange={(e) =>
                                    handleParameterChange(param.id, e.target.value, param)
                                }
                                placeholder={`Enter ${param.name.toLowerCase()}${placeholderSuffix}`}
                                style={{
                                    width: "100%",
                                    padding: "8px",
                                    borderRadius: "4px",
                                    border: borderStyle,
                                    fontSize: "14px",
                                    backgroundColor: "white",
                                    outline: "none",
                                    transition: "border-color 0.2s",
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = "#3182CE";
                                    e.target.style.boxShadow = "0 0 0 1px #3182CE";
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = computedBorderColor;
                                    e.target.style.boxShadow = "none";
                                }}
                                {...inputProps}
                            />
                            {hasValue && (
                                <span
                                    style={{
                                        position: "absolute",
                                        right: "8px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#38A169",
                                        fontSize: "12px",
                                    }}
                                >
                  ✓
                </span>
                            )}
                        </div>
                        {showPatientHint && (
                            <div
                                style={{
                                    color: "#4A5568",
                                    fontSize: "12px",
                                    marginTop: "4px",
                                }}
                            >
                            </div>
                        )}
                        {error && (
                            <div
                                style={{color: "#E53E3E", fontSize: "12px", marginTop: "4px"}}
                            >
                                {error}
                            </div>
                        )}
                    </div>
                </td>
            );
        };

        return (
            <table width="100%" style={{borderCollapse: "separate", borderSpacing: "0 12px"}}>
                <tbody>
                {/* First Row */}
                <tr>
                    {firstRow.map((param) => renderParameterInput(param))}
                    {/* Fill remaining cells if first row has fewer parameters */}
                    {firstRow.length < midPoint &&
                        Array.from({length: midPoint - firstRow.length}, (_, index) => (
                            <td
                                key={`empty-${index}`}
                                style={{
                                    border: "none",
                                    padding: "10px",
                                    background: "white",
                                    boxShadow: "0 8px 24px rgba(15,23,42,0.02)",
                                }}
                            ></td>
                        ))}
                </tr>

                {/* Second Row */}
                {secondRow.length > 0 && (
                    <tr>
                        {secondRow.map((param) => renderParameterInput(param))}
                        {/* Fill remaining cells if second row has fewer parameters */}
                        {secondRow.length < midPoint &&
                            Array.from(
                                {length: midPoint - secondRow.length},
                                (_, index) => (
                                    <td
                                        key={`empty-second-${index}`}
                                        style={{
                                            border: "1px solid #E2E8F0",
                                            padding: "10px",
                                            background: "white",
                                        }}
                                    ></td>
                                )
                            )}
                    </tr>
                )}
                </tbody>
            </table>
        );
    };

    // Removed blocking beforeunload confirmation so users can reload freely.
    // Previously the page blocked reload/navigation when `hasUnsavedChanges` was true.
    // We now rely on in-app UI state and toasts for warnings, without preventing unload.

    // Main component render
    return (
        <tr key={String(hasUnsavedChanges)}>
            <td
                style={{
                    border: "1px solid #E2E8F0",
                    padding: "10px",
                    background: "white",
                    textAlign: "left",
                }}
            >
                <Text fontWeight="bold" style={{fontSize: "16px"}}>
                    Parameters:
                </Text>
            </td>
            <td
                style={{
                    border: "1px solid #E2E8F0",
                    padding: "10px",
                    background: "white",
                    textAlign: "left",
                }}
            >
                {isLoading ? (
                    <div style={{textAlign: "center", padding: "20px"}}>
                        <div
                            style={{
                                display: "inline-block",
                                width: "20px",
                                height: "20px",
                                border: "2px solid #f3f3f3",
                                borderTop: "2px solid #3498db",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                            }}
                        ></div>
                        <div style={{marginTop: "10px", color: "#666"}}>
                            Loading parameters...
                        </div>
                    </div>
                ) : error ? (
                    <div
                        style={{
                            backgroundColor: "#FED7D7",
                            border: "1px solid #FC8181",
                            borderRadius: "4px",
                            padding: "10px",
                            color: "#C53030",
                        }}
                    >
                        <div style={{fontWeight: "bold", marginBottom: "5px"}}>
                            Error loading parameters
                        </div>
                        <div style={{fontSize: "14px"}}>{error}</div>
                        <button
                            onClick={initializeParameters}
                            style={{
                                marginTop: "10px",
                                backgroundColor: "#E53E3E",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                padding: "6px 12px",
                                fontSize: "12px",
                                cursor: "pointer",
                            }}
                        >
                            Retry
                        </button>
                    </div>
                ) : parameterDefinitions.length === 0 ? (
                    <div style={{color: "#666", padding: "20px", textAlign: "center"}}>
                        No parameters configured for this doctor
                    </div>
                ) : (
                    <div>
                        {renderParameterTable()}
                        <div style={{textAlign: "right", marginTop: "15px"}}>
                            {hasUnsavedChanges && (
                                <span
                                    style={{
                                        fontSize: "15px",
                                        color: "#E53E3E",
                                        marginRight: "10px",
                                    }}
                                >
                  {/*Unsaved changes*/}
                </span>)}
                            {/* Update button removed — parameters are saved on End Consultation */}
                        </div>
                    </div>
                )}
            </td>
        </tr>
    );
};

// Prop types
ParametersSection.propTypes = {
    appointment_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    doctor_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onAddparameters: PropTypes.func,
    setparametersList: PropTypes.func,
};

// Default props
ParametersSection.defaultProps = {
    appointment_id: null,
    doctor_id: null,
    onAddparameters: null,
    setparametersList: null,
};

export default ParametersSection;
